import { hour24, type SegmentOptions, type SegmentValues } from './segments';
import { clampTime, timeKey, type WallTime } from './temporal';

/** A time column: the hours, the minutes or AM/PM. */
export type TimeColumnType = 'hour' | 'minute' | 'period';

export interface TimeBounds {
  min: WallTime | null;
  max: WallTime | null;
}

const overlaps = (from: number, to: number, bounds: TimeBounds) =>
  to >= (bounds.min ? timeKey(bounds.min) : 0) && from <= (bounds.max ? timeKey(bounds.max) : 1439);

/** The options a column lists, in order: 12, 1 … 11 or 0 … 23; minutes by the step; AM, PM. */
export function columnOptions(type: TimeColumnType, options: SegmentOptions): number[] {
  if (type === 'hour') {
    return options.hour12
      ? [12, ...Array.from({ length: 11 }, (_, i) => i + 1)]
      : Array.from({ length: 24 }, (_, i) => i);
  }
  if (type === 'minute') {
    const step = Math.max(1, options.minuteStep);
    return Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step);
  }
  return [0, 1];
}

/**
 * Whether a column option lies wholly outside the bounds. An hour is checked within the chosen
 * period (AM when none), a minute within the chosen hour (none disabled before one is chosen).
 */
export function isTimeOptionDisabled(
  type: TimeColumnType,
  option: number,
  values: SegmentValues,
  options: SegmentOptions,
  bounds: TimeBounds,
): boolean {
  if (!bounds.min && !bounds.max) return false;
  if (type === 'period') return !overlaps(option * 720, option * 720 + 719, bounds);
  if (type === 'hour') {
    const hour = options.hour12 ? (option % 12) + (values.period === 1 ? 12 : 0) : option;
    return !overlaps(hour * 60, hour * 60 + 59, bounds);
  }
  const hour = hour24(values, options);
  if (hour === undefined) return false;
  const key = hour * 60 + option;
  return !overlaps(key, key, bounds);
}

/**
 * The segments after picking a column option: missing parts are filled (12 AM or 0:00, minute
 * 0) and the time is pulled into the bounds, so a pick never produces an out-of-range time.
 */
export function pickTimeOption(
  type: TimeColumnType,
  option: number,
  values: SegmentValues,
  options: SegmentOptions,
  bounds: TimeBounds,
): WallTime {
  const next: SegmentValues = { ...values, [type]: option };
  const filled: SegmentValues = {
    hour: next.hour ?? (options.hour12 ? 12 : 0),
    minute: next.minute ?? 0,
    period: next.period ?? 0,
  };
  const hour = hour24(filled, options) ?? 0;
  return clampTime({ hour, minute: filled.minute ?? 0 }, bounds.min, bounds.max);
}
